import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Button,
  Switch
} from '@mui/material';
import { 
  Timeline as TimelineIcon,
  TableChart as TableIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import { useTableSort } from '../../../../hooks/useTableSort';
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader';
import RiskChart from './RiskChart';

// Types
interface RiskIndicator {
  RIInfoID: number;
  BackstopID: number;
  FundName: string;
  StrategyName: string;
  StyleName: string;
  MonitorDate: string;
  RIName: string;
  RIValue: number;
  ThresholdValue: number;
  IsBreached: boolean;
  RITypeID: number;
}

interface Strategy {
  StrategyID: number;
  StrategyName: string;
}

interface Style {
  StyleID: number;
  StyleName: string;
}

interface MonitorDate {
  MonitorDate: string;
  DateDisplay: string;
}

const RiskMonitoring: React.FC = () => {
  const { getAccessToken } = useAuth();
  const { 
    strategies, 
    styles, 
    monitorDates, 
    riskIndicators, 
    loading, 
    refreshRiskIndicators 
  } = useHedgeFundData();
  
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to track ongoing requests and prevent cancellation on unmount
  const activeRequestRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  
  // Filter state
  const [selectedStrategy, setSelectedStrategy] = useState<string>('');
  const [selectedStyles, setSelectedStyles] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Risk indicator type filters
  const [showAUM, setShowAUM] = useState(true);
  const [showConcentration, setShowConcentration] = useState(true);
  const [showLeverage, setShowLeverage] = useState(true);
  const [showLiquidity, setShowLiquidity] = useState(true);
  const [showMarketRisk, setShowMarketRisk] = useState(true);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // View state
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [portfolioWeightedData, setPortfolioWeightedData] = useState<any[]>([]);
  const [showBreachedOnly, setShowBreachedOnly] = useState(false);
  const [loadingChartData, setLoadingChartData] = useState(false);

  // Check if all filters have loaded from context
  const allFiltersLoaded = !loading.strategies && !loading.styles && !loading.monitorDates;

  // Set default dates when monitor dates are loaded
  useEffect(() => {
    if (monitorDates.length > 0 && !startDate && !endDate) {
      // Set end date to most recent date
      setEndDate(monitorDates[0].MonitorDate);
      
      // Set start date to two years before today
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      const twoYearsAgoString = twoYearsAgo.toISOString().split('T')[0];
      
      // Find the closest available date to two years ago, or use the oldest available date
      const availableStartDate = monitorDates.find(date => date.MonitorDate >= twoYearsAgoString) 
        || monitorDates[monitorDates.length - 1]; // Fallback to oldest date
      
      setStartDate(availableStartDate.MonitorDate);
    }
  }, [monitorDates, startDate, endDate]);

  // Load risk indicators when dates are set and filters are ready
  useEffect(() => {
    if (startDate && endDate && allFiltersLoaded) {
      loadRiskIndicators();
    }
  }, [startDate, endDate, allFiltersLoaded]);

  // Load portfolio weighted data for charts only when switching to chart view
  useEffect(() => {
    if (startDate && endDate && viewMode === 'chart') {
      loadPortfolioWeightedData();
    }
  }, [viewMode]); // Only trigger when view mode changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    };
  }, []);

  const loadRiskIndicators = async () => {
    // Cancel any existing request
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    activeRequestRef.current = abortController;

    setError(null);
    
    try {
      // Build filters for context refresh
      const filters: {
        strategy_id?: number;
        style_ids?: number[];
        start_date?: string;
        end_date?: string;
        ri_types?: number[];
      } = {};
      
      if (selectedStrategy) filters.strategy_id = parseInt(selectedStrategy);
      if (selectedStyles.length > 0) filters.style_ids = selectedStyles;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      
      // Risk indicator types
      const riTypes = [];
      if (showAUM) riTypes.push(1);
      if (showConcentration) riTypes.push(2);
      if (showLeverage) riTypes.push(3);
      if (showLiquidity) riTypes.push(4);
      if (showMarketRisk) riTypes.push(5);
      
      if (riTypes.length > 0) filters.ri_types = riTypes;

      // Use context refresh function instead of direct API call
      await refreshRiskIndicators(filters);
      
    } catch (err) {
      // Only handle errors if not aborted and component is still mounted
      if (err instanceof Error && err.name !== 'AbortError' && isMountedRef.current) {
        setError(err.message);
      }
    } finally {
      // Clean up request reference
      if (isMountedRef.current && activeRequestRef.current === abortController) {
        activeRequestRef.current = null;
      }
    }
  };

  const loadPortfolioWeightedData = async () => {
    setLoadingChartData(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      if (selectedStrategy) params.append('strategy_ids', selectedStrategy);
      if (selectedStyles.length > 0) params.append('style_ids', selectedStyles.join(','));

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/risk-analysis/portfolio-weighted?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load portfolio weighted data');
      }

      const result = await response.json();
      setPortfolioWeightedData(result.data || []);
    } catch (err) {
      console.error('Failed to load portfolio weighted data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load chart data');
      setPortfolioWeightedData([]);
    } finally {
      setLoadingChartData(false);
    }
  };

  const handleStyleChange = (styleId: number, checked: boolean) => {
    if (checked) {
      setSelectedStyles(prev => [...prev, styleId]);
    } else {
      setSelectedStyles(prev => prev.filter(id => id !== styleId));
    }
  };

  const handleRiskTypeChange = (type: string, checked: boolean) => {
    switch (type) {
      case 'AUM':
        setShowAUM(checked);
        break;
      case 'Concentration':
        setShowConcentration(checked);
        break;
      case 'Leverage':
        setShowLeverage(checked);
        break;
      case 'Liquidity':
        setShowLiquidity(checked);
        break;
      case 'MarketRisk':
        setShowMarketRisk(checked);
        break;
    }
  };

  const getRiskTypeColor = (riTypeId: number) => {
    switch (riTypeId) {
      case 1: return '#FF0000'; // AUM - Red
      case 2: return '#00FF00'; // Concentration - Green  
      case 3: return '#0000FF'; // Leverage - Blue
      case 4: return '#FFFF00'; // Liquidity - Yellow
      case 5: return '#800080'; // Market Risk - Purple
      default: return '#000000';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatValue = (value: number) => {
    return value.toFixed(4);
  };

  // Filter data first
  const filteredData = showBreachedOnly 
    ? riskIndicators.filter(indicator => indicator.IsBreached)
    : riskIndicators;

  // Apply sorting to filtered data
  const { sortedData, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredData,
    initialSort: [{ field: 'MonitorDate', direction: 'desc' }] // Default sort by date descending
  });

  // Paginate sorted data
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  // Calculate breach count
  const breachedCount = riskIndicators.filter(indicator => indicator.IsBreached).length;
  

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Risk Monitoring
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Filters Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Filters
            </Typography>
            
            <Grid container spacing={1.5} alignItems="center">
              {/* Date Range */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Start Date</InputLabel>
                  <Select
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    label="Start Date"
                  >
                    {monitorDates.map(date => (
                      <MenuItem key={date.MonitorDate} value={date.MonitorDate}>
                        {date.DateDisplay}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>End Date</InputLabel>
                  <Select
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    label="End Date"
                  >
                    {monitorDates.map(date => (
                      <MenuItem key={date.MonitorDate} value={date.MonitorDate}>
                        {date.DateDisplay}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Strategy Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Strategy</InputLabel>
                  <Select
                    value={selectedStrategy}
                    onChange={(e) => setSelectedStrategy(e.target.value)}
                    label="Strategy"
                  >
                    <MenuItem value="">All Strategies</MenuItem>
                    {strategies.map(strategy => (
                      <MenuItem key={strategy.StrategyID} value={strategy.StrategyID.toString()}>
                        {strategy.StrategyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Style Filter */}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                Style Filter
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {styles.map(style => (
                  <FormControlLabel
                    key={style.StyleID}
                    control={
                      <Checkbox
                        checked={selectedStyles.includes(style.StyleID)}
                        onChange={(e) => handleStyleChange(style.StyleID, e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{style.StyleName}</Typography>}
                  />
                ))}
              </Box>
            </Box>

            {/* Risk Indicator Type Filter */}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                Risk Indicator Types
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAUM}
                      onChange={(e) => handleRiskTypeChange('AUM', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">AUM</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showConcentration}
                      onChange={(e) => handleRiskTypeChange('Concentration', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Concentration</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showLeverage}
                      onChange={(e) => handleRiskTypeChange('Leverage', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Leverage</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showLiquidity}
                      onChange={(e) => handleRiskTypeChange('Liquidity', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Liquidity</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showMarketRisk}
                      onChange={(e) => handleRiskTypeChange('MarketRisk', e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Market Risk</Typography>}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, position: 'relative' }}>
            {/* Loading overlay when filters are loading */}
            {!allFiltersLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  borderRadius: 1
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Loading Filters...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please wait while we load the available strategies, styles, and dates.
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6">
                  Risk Indicators ({filteredData.length} records)
                </Typography>
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                  Breached - {breachedCount}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showBreachedOnly}
                      onChange={(e) => {
                        setShowBreachedOnly(e.target.checked);
                        setPage(0); // Reset to first page when filtering
                      }}
                      size="small"
                      disabled={!allFiltersLoaded}
                    />
                  }
                  label="Show breached only"
                  sx={{ ml: 1 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableIcon />
                  <Switch
                    checked={viewMode === 'chart'}
                    onChange={(e) => setViewMode(e.target.checked ? 'chart' : 'table')}
                    disabled={!allFiltersLoaded}
                  />
                  <TimelineIcon />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => viewMode === 'table' ? loadRiskIndicators() : loadPortfolioWeightedData()}
                  disabled={loading.riskIndicators || !allFiltersLoaded}
                >
                  Refresh
                </Button>
                {loading.riskIndicators && <CircularProgress size={24} />}
              </Box>
            </Box>

            {viewMode === 'chart' ? (
              loadingChartData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading chart data...
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <RiskChart data={portfolioWeightedData} />
              )
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <SortableTableHeader
                          field="FundName"
                          label="Fund"
                          sortDirection={getSortDirection('FundName')}
                          sortIndex={getSortIndex('FundName')}
                          onSort={handleSort}
                        />
                        <SortableTableHeader
                          field="StrategyName"
                          label="Strategy"
                          sortDirection={getSortDirection('StrategyName')}
                          sortIndex={getSortIndex('StrategyName')}
                          onSort={handleSort}
                        />
                        <SortableTableHeader
                          field="StyleName"
                          label="Style"
                          sortDirection={getSortDirection('StyleName')}
                          sortIndex={getSortIndex('StyleName')}
                          onSort={handleSort}
                        />
                        <SortableTableHeader
                          field="MonitorDate"
                          label="Date"
                          sortDirection={getSortDirection('MonitorDate')}
                          sortIndex={getSortIndex('MonitorDate')}
                          onSort={handleSort}
                        />
                        <SortableTableHeader
                          field="RIName"
                          label="Risk Indicator"
                          sortDirection={getSortDirection('RIName')}
                          sortIndex={getSortIndex('RIName')}
                          onSort={handleSort}
                        />
                        <SortableTableHeader
                          field="RIValue"
                          label="Value"
                          sortDirection={getSortDirection('RIValue')}
                          sortIndex={getSortIndex('RIValue')}
                          onSort={handleSort}
                          align="right"
                        />
                        <SortableTableHeader
                          field="ThresholdValue"
                          label="Threshold"
                          sortDirection={getSortDirection('ThresholdValue')}
                          sortIndex={getSortIndex('ThresholdValue')}
                          onSort={handleSort}
                          align="right"
                        />
                        <SortableTableHeader
                          field="IsBreached"
                          label="Status"
                          sortDirection={getSortDirection('IsBreached')}
                          sortIndex={getSortIndex('IsBreached')}
                          onSort={handleSort}
                        />
                      </TableRow>
                    </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {loading.riskIndicators ? 'Loading risk indicators...' : 'No risk indicators found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((indicator, index) => (
                      <TableRow key={`${indicator.RIInfoID}-${index}`}>
                        <TableCell>{indicator.FundName}</TableCell>
                        <TableCell>{indicator.StrategyName}</TableCell>
                        <TableCell>{indicator.StyleName}</TableCell>
                        <TableCell>{formatDate(indicator.MonitorDate)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getRiskTypeColor(indicator.RITypeID)
                              }}
                            />
                            {indicator.RIName}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatValue(indicator.RIValue)}</TableCell>
                        <TableCell align="right">{formatValue(indicator.ThresholdValue)}</TableCell>
                        <TableCell>
                          <Chip
                            label={indicator.IsBreached ? 'Breached' : 'OK'}
                            color={indicator.IsBreached ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};


export default RiskMonitoring;
